import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Button,
  Fade,
  Grow,
  Menu,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  HelpOutline as HelpIcon,
  EmojiObjects as TipsIcon,
  Message as MessageIcon,
  ChevronLeft as ChevronLeftIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../contexts/ThemeContext';
import apiService from '../../services/apiService';
import { Translate as TranslateIcon, History as HistoryIcon } from '@mui/icons-material';
import LanguageModal from '../../components/Common/LanguageModal';

interface FAQ {
  id: number;
  category: string;
  question: string;
  answer: string;
  tags: string[];
}

interface Language {
  code: string;
  name: string;
  flag: string | any;
  isImage?: boolean;
}

const HelpPage: React.FC = () => {
  const { t } = useTranslation();
  const { actualMode: mode } = useThemeContext();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<number | false>(false);
  const [translations, setTranslations] = useState<Record<number, { question: string, answer: string, showOriginal: boolean }>>({});
  const [translatingId, setTranslatingId] = useState<number | null>(null);
  const faqs = t('helpPage.faqs', { returnObjects: true }) as FAQ[];

  const [targetLang, setTargetLang] = useState<Language>({ code: 'en', name: 'English', flag: '🇺🇸' });
  const [anchorEl] = useState<null | HTMLElement>(null);



  const handleLanguageSelect = (lang: any) => {
    setTargetLang(lang);
    setLangModalVisible(false);
    setTranslations({});
  };

  const [langModalVisible, setLangModalVisible] = useState(false);

  const filteredFaqs = useMemo(() => {
    if (!searchQuery.trim()) return faqs;
    const query = searchQuery.toLowerCase();
    return faqs.filter(
      faq =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.category.toLowerCase().includes(query) ||
        faq.tags.some(tag => tag.toLowerCase().includes(query))
    );
  }, [searchQuery, faqs]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    faqs.forEach(faq => cats.add(faq.category));
    return Array.from(cats);
  }, [faqs]);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleCategoryClick = (cat: string) => {
    setSearchQuery(cat);
  };

  const handleTranslateFaq = async (id: number, question: string, answer: string) => {
    if (translations[id]) {
      setTranslations(prev => ({
        ...prev,
        [id]: { ...prev[id], showOriginal: !prev[id].showOriginal }
      }));
      return;
    }

    try {
      setTranslatingId(id);
      const [qRes, aRes] = await Promise.all([
        apiService.translateText(question, targetLang.code),
        apiService.translateText(answer, targetLang.code)
      ]);

      if (qRes.success && aRes.success) {
        setTranslations(prev => ({
          ...prev,
          [id]: {
            question: qRes.data.translatedText,
            answer: aRes.data.translatedText,
            showOriginal: true
          }
        }));
      }
    } catch (err) {
      console.error('FAQ translation failed:', err);
    } finally {
      setTranslatingId(null);
    }
  };

  return (
    <Box sx={{
      flexGrow: 1,
      p: { xs: 2, md: 6 },
      minHeight: '100vh',
      backgroundColor: mode === 'dark' ? '#000' : '#f5f7fa',
      backgroundImage: mode === 'dark'
        ? 'radial-gradient(circle at 50% 0%, rgba(16, 185, 129, 0.05) 0%, transparent 50%)'
        : 'radial-gradient(circle at 50% 0%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)'
    }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Button
            startIcon={<ChevronLeftIcon />}
            onClick={() => navigate('/support')}
            sx={{
              mb: 4,
              color: 'text.secondary',
              fontWeight: 700,
              fontSize: '1.1rem',
              '&:hover': { background: 'rgba(0,0,0,0.05)', transform: 'translateX(-5px)' },
              transition: 'all 0.2s ease'
            }}
          >
            {t('helpPage.backToSupport', { defaultValue: 'Back to Support' })}
          </Button>

          <Typography
            variant="h1"
            sx={{
              fontWeight: 900,
              mb: 2,
              fontSize: { xs: '3.5rem', md: '5rem' },
              background: 'linear-gradient(135deg, #10B981 0%, #3B82F6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.03em',
            }}
          >
            {t('helpPage.title', { defaultValue: 'Help Center & FAQ' })}
          </Typography>

          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setLangModalVisible(true)}
              startIcon={<TranslateIcon />}
              sx={{ borderRadius: '12px', fontWeight: 'bold' }}
            >
              {targetLang.isImage ? (
                <Box component="img" src={targetLang.flag} sx={{ width: 24, height: 18, borderRadius: '2px', mr: 1 }} />
              ) : (
                <Box component="span" sx={{ mr: 1 }}>{targetLang.flag}</Box>
              )}
              {targetLang.name}
            </Button>
            <LanguageModal
              open={langModalVisible}
              onClose={() => setLangModalVisible(false)}
              onSelect={handleLanguageSelect}
              title={t('navbar.language', 'Select Language')}
              selectedCode={targetLang.code}
            />
          </Box>

          <Alert severity="info" sx={{ mb: 4, borderRadius: '24px', fontSize: '2rem !important', fontWeight: '800', border: '3px solid #3b82f6', color: '#1d4ed8' }}>
            {t('termsPage.legalDisclaimer', '🛡️ This translation is provided for your convenience only. In the case of any discrepancies, the English version remains the only legally binding document.')}
          </Alert>

          <Typography variant="h5" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto', fontWeight: 500, lineHeight: 1.6 }}>
            {t('helpPage.subtitle', { defaultValue: 'Find answers, get tips, and learn how to make the most of Ride Club.' })}
          </Typography>
        </Box>

        {/* Search Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 1,
            mb: 6,
            borderRadius: '32px',
            background: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.1)'
          }}
        >
          <TextField
            fullWidth
            placeholder={t('helpPage.searchPlaceholder', { defaultValue: 'Search for questions, categories, or keywords...' })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'primary.main', fontSize: '2rem', ml: 2, mr: 1 }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: '28px',
                fontSize: '1.4rem',
                fontWeight: 600,
                '& fieldset': { border: 'none' },
                py: 1
              }
            }}
          />
        </Paper>

        {/* Quick Topics */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 900, color: 'text.secondary', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {t('helpPage.browseCategories', { defaultValue: 'Browse by Category' })}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                onClick={() => handleCategoryClick(cat)}
                sx={{
                  px: 3,
                  py: 3,
                  fontSize: '1.1rem',
                  fontWeight: 800,
                  borderRadius: '16px',
                  backgroundColor: searchQuery === cat ? 'primary.main' : (mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'white'),
                  color: searchQuery === cat ? 'white' : 'text.primary',
                  border: '1px solid',
                  borderColor: searchQuery === cat ? 'primary.main' : 'divider',
                  '&:hover': {
                    backgroundColor: searchQuery === cat ? 'primary.dark' : 'rgba(16, 185, 129, 0.1)',
                    borderColor: 'primary.main',
                    transform: 'translateY(-3px)'
                  },
                  transition: 'all 0.2s ease'
                }}
              />
            ))}
          </Box>
        </Box>

        {/* FAQ Accordions */}
        <Box sx={{ mb: 8 }}>
          <Box>
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => (
                <Grow in key={faq.id} timeout={300 + index * 50}>
                  <Accordion
                    expanded={expanded === faq.id}
                    onChange={handleChange(faq.id)}
                    sx={{
                      mb: 2,
                      borderRadius: '24px !important',
                      border: '1px solid',
                      borderColor: expanded === faq.id ? 'primary.main' : 'divider',
                      boxShadow: expanded === faq.id ? '0 15px 30px -10px rgba(16, 185, 129, 0.2)' : 'none',
                      backgroundColor: mode === 'dark' ? 'rgba(20, 20, 20, 0.4)' : 'white',
                      '&:before': { display: 'none' },
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main', fontSize: '2rem' }} />}
                      sx={{ p: { xs: 2, md: 3 } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%' }}>
                        <Box sx={{
                          p: 1.5,
                          borderRadius: '15px',
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          display: { xs: 'none', sm: 'flex' }
                        }}>
                          <HelpIcon sx={{ color: 'primary.main', fontSize: '1.8rem' }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{
                            fontWeight: 800,
                            color: 'primary.main',
                            textTransform: 'uppercase',
                            mb: 0.5,
                            fontSize: '1.35rem',
                            letterSpacing: '0.05em'
                          }}>
                            {faq.category}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: '2.7rem', md: '3.15rem' } }}>
                            {translations[faq.id]?.showOriginal ? translations[faq.id].question : faq.question}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTranslateFaq(faq.id, faq.question, faq.answer);
                        }}
                        disabled={translatingId === faq.id}
                        startIcon={translatingId === faq.id ? <TranslateIcon className="animate-pulse" /> : (translations[faq.id]?.showOriginal ? <HistoryIcon /> : <TranslateIcon />)}
                        sx={{
                          ml: 'auto',
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: '900',
                          borderWidth: '2px',
                          color: translations[faq.id]?.showOriginal ? 'primary.main' : 'text.secondary',
                          '&:hover': { borderWidth: '2px' }
                        }}
                      >
                        {translatingId === faq.id ? t('common.translating') : (translations[faq.id]?.showOriginal ? t('common.showOriginal') : `${t('common.translate')} to ${targetLang.name}`)}
                      </Button>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: { xs: 2.5, md: 10 }, pb: 5 }}>
                      <Typography variant="h6" sx={{
                        color: 'text.secondary',
                        lineHeight: 1.8,
                        fontWeight: 500,
                        fontSize: '2.7rem',
                        mb: 3
                      }}>
                        {translations[faq.id]?.showOriginal ? translations[faq.id].answer : faq.answer}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {faq.tags.map(tag => (
                          <Chip
                            key={tag}
                            label={`#${tag}`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              backgroundColor: 'transparent',
                              border: '1px solid',
                              borderColor: 'divider',
                              color: 'text.secondary'
                            }}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grow>
              ))
            ) : (
              <Fade in>
                <Box sx={{ textAlign: 'center', py: 10 }}>
                  <Typography variant="h2" sx={{ mb: 2 }}>🤔</Typography>
                  <Typography variant="h4" fontWeight="900" sx={{ mb: 2 }}>
                    {t('helpPage.noResults', { defaultValue: 'No results for your "vibe check"' })}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {t('helpPage.noResultsDesc', { defaultValue: 'Try different keywords or browse the categories above.' })}
                  </Typography>
                </Box>
              </Fade>
            )}
          </Box>
        </Box>

        {/* CTA Section */}
        <Paper
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: '40px',
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            color: 'white',
            textAlign: 'center',
            boxShadow: '0 30px 60px -15px rgba(16, 185, 129, 0.4)'
          }}
        >
          <TipsIcon sx={{ fontSize: '4rem', mb: 3 }} />
          <Typography variant="h3" fontWeight="900" sx={{ mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
            {t('helpPage.stillConfused', { defaultValue: 'Still feeling confused?' })}
          </Typography>
          <Typography variant="h5" sx={{ mb: 5, opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
            {t('helpPage.contactUsCta', { defaultValue: 'If you couldn\'t find your answer here, our team is ready to help you directly.' })}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<MessageIcon />}
            onClick={() => navigate('/support')}
            sx={{
              backgroundColor: 'white',
              color: '#10B981',
              py: 2.5,
              px: 6,
              borderRadius: '20px',
              fontSize: '1.4rem',
              fontWeight: 900,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#f0fdf4',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('helpPage.getDirectSupport', { defaultValue: 'Get Direct Support' })}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default HelpPage;